import { FC, SyntheticEvent, useContext, useEffect } from 'react';
import GlobalContext from '../../contexts/GlobalContext';
import Register3rdPartyContext, { ERegister3rdPartyUiState } from '../../contexts/Register3rdPartyContext';
import LegalAgreements from '../LegalAgreements/LegalAgreements';
import { Box, Divider, Icon, Link, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PageLayoutV2 from '../../layout/PageLayoutV2/PageLayoutV2';
import ScreenHeader from '../ScreenHeader/ScreenHeader';
import mixpanel from 'mixpanel-browser';
import { EMixpanelEvent } from '../../util/constants';

const Register3rdPartyForm: FC = () => {
    const { language, coBranding, countriesList } = useContext(GlobalContext);
    const {
        canShowUsername,
        firstName,
        setFirstName,
        firstNameIsValid,
        firstNameValidationMessage,
        lastName,
        setLastName,
        lastNameIsValid,
        lastNameValidationMessage,
        selectedCountryCode,
        setSelectedCountryCode,
        countryIsValid,
        register3rdPartyUiState,
        submitRegisterForm,
        addFamily,
        skipAddFamily,
    } = useContext(Register3rdPartyContext);

    const handleFormSubmit = (event) => {
        event.preventDefault();
        submitRegisterForm();
    };

    const handleAddFamily = (event: SyntheticEvent) => {
        event.preventDefault();
        addFamily();
    };

    const handleFamilyFirstSkipFamily = (event: SyntheticEvent) => {
        event.preventDefault();
        skipAddFamily();
    };

    useEffect(() => {
        mixpanel.track(EMixpanelEvent.actionStart, { key: 'consumer_create_account' });
    }, []);

    return (
        <PageLayoutV2 testId="external-register">
            <ScreenHeader
                title={
                    coBranding.isDefaultFamilyAccount
                        ? language.createAccount.headerFamilyFirst
                        : language.createAccount.header
                }
                desc={
                    coBranding.isDefaultFamilyAccount
                        ? language.createAccount.descFamilyFirst
                        : language.createAccount.desc
                }
            />
            {coBranding.isDefaultFamilyAccount && (
                <Stack direction={'row'} sx={{ my: 2, alignItems: 'center' }}>
                    <Typography component={'h3'} variant={'subtitle2'}>
                        {language.createFamilyAccount.primaryUser.title}
                    </Typography>
                    <Tooltip title={language.createFamilyAccount.details}>
                        <Icon color="action" fontSize={'xSmall'} sx={{ ml: 1 }}>
                            info
                        </Icon>
                    </Tooltip>
                </Stack>
            )}
            <form>
                <Box>
                    {canShowUsername && (
                        <>
                            <TextField
                                label={language.register.firstName}
                                value={firstName}
                                onChange={(event) => setFirstName(event.target.value)}
                                error={!firstNameIsValid}
                                helperText={!firstNameIsValid && firstNameValidationMessage}
                                fullWidth
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label={language.register.lastName}
                                value={lastName}
                                onChange={(event) => setLastName(event.target.value)}
                                error={!lastNameIsValid}
                                helperText={!lastNameIsValid && lastNameValidationMessage}
                                fullWidth
                                sx={{ mb: 2 }}
                            />
                        </>
                    )}

                    <TextField
                        select
                        label={language.common.country}
                        fullWidth
                        sx={{ mb: 2 }}
                        value={selectedCountryCode}
                        onChange={(event) => setSelectedCountryCode(event.target.value)}
                        error={!countryIsValid}
                    >
                        {countriesList.map((country) => (
                            <MenuItem key={country.code} value={country.code}>
                                {country.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
                {coBranding.isDefaultFamilyAccount ? (
                    <Box>
                        <LoadingButton
                            variant="contained"
                            type="submit"
                            onClick={handleAddFamily}
                            size="large"
                            fullWidth
                        >
                            {language.createAccount.createFamilyCta}
                        </LoadingButton>
                        <Divider sx={{ my: 3, typography: 'body2' }}>{language.common.or}</Divider>

                        <Stack direction={'row'} sx={{ justifyContent: 'center' }}>
                            <Link
                                component={'button'}
                                variant="subtitle2"
                                onClick={handleFamilyFirstSkipFamily}
                                sx={{ display: 'block' }}
                            >
                                {language.createAccount.familyFirstSkipFamily}
                            </Link>
                        </Stack>
                    </Box>
                ) : (
                    <>
                        <LoadingButton
                            type="submit"
                            variant="contained"
                            onClick={handleFormSubmit}
                            disabled={
                                register3rdPartyUiState === ERegister3rdPartyUiState.loading ||
                                register3rdPartyUiState === ERegister3rdPartyUiState.success
                            }
                            loading={
                                register3rdPartyUiState === ERegister3rdPartyUiState.loading ||
                                register3rdPartyUiState === ERegister3rdPartyUiState.success
                            }
                            size="large"
                            fullWidth
                        >
                            {language.register.createAccountCTA}
                        </LoadingButton>
                        <Box sx={{ my: 2 }}>
                            <LegalAgreements />
                        </Box>
                    </>
                )}
            </form>
        </PageLayoutV2>
    );
};

export default Register3rdPartyForm;
